/**
 * The whole simulator composition: thread + Ask AI, sidebar, conversation picker, past
 * runs, reset. The simulator page renders it full page, back testing and a workflow each in a
 * sheet. Run state (thread, timeline, run details, socket handler) is a global singleton,
 * so cases cannot be switched mid-run without two runs interleaving into one thread.
 */

import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { lastRunDetailsAtom, resetSimulatorAtom, thinkingStepsAtom } from '@/stores/websocket.store';
import { useTicketThread } from '@/hooks';
import { RunReview } from './review/run-review';
import { comparedReplyView } from './review/compared-reply-view';
import { replySideAtom } from './review/reply-side';
import { missingPhrases, num } from '@/modules/review/verdict-readers';
import { runReviewsAtom } from './review/review.store';
import { messageService } from '@/services/message.service';
import { Simulator } from '@/modules/simulator/thread/simulator';
import type { SimulatorRole } from '@/modules/simulator/thread/roles';
import { otherRole } from '@/modules/simulator/thread/roles';
import { SimulatorSidebar } from '@/modules/simulator/sidebar/simulator-sidebar';
import { DescribePanel, DescribeTrigger } from '@/modules/simulator/describe/describe-situation';
import { DescribedScenarioChip } from '@/modules/simulator/describe/described-scenario-chip';
import { cancelDescribeFlowAtom, describeFlowAtom } from '@/modules/simulator/describe/describe-flow';
import {
  simulationProfileAtom,
  simulatorCustomerAtom,
  simulateToolsAtom,
  buildSimulationProfile,
  EMPTY_SIMULATION_PROFILE,
  openSectionsAtom,
} from '@/modules/simulator/sidebar/simulation-profile.store';
import type { ScenarioCase } from '@/modules/simulator/session/scenario-case';
import { filledSections } from '@/modules/simulator/session/scenario-case';
import { ScenarioCaseLoader } from '@/modules/simulator/session/scenario-case-loader';
import type { CaseRun } from '@/modules/simulator/session/simulator-session.store';
import {
  nextConversationId,
  simulatorSessionAtom,
  clearActiveCaseAtom,
  endSimulatorSessionAtom,
  caseOnScreenAtom,
  channelIdAtom,
  conversationIdAtom,
  loadedThreadAtom,
  reopenedLinksAtom,
  describedScenarioAtom,
  EMPTY_DESCRIBED_SCENARIO,
  runOnScreenAtom,
} from '@/modules/simulator/session/simulator-session.store';
import { useCaseOnScreen } from '@/modules/simulator/session/use-case-on-screen';
import { useToolModeLock } from '@/modules/simulator/session/use-tool-mode-lock';
import { restoreRunAtom } from '@/modules/simulator/session/restore-run';
import { ConversationPicker } from '@/modules/simulator/conversations/conversation-picker';
import { PastRunsSheet } from '@/modules/simulator/runs/past-runs-sheet';
import { ticketService } from '@/services/ticket.service';
import type { SimulatorRun } from '@/services/simulator-run.service';
import { threadFromConversation, threadFromTicket, type LoadedThread } from '@/modules/simulator/conversations/loaded-thread';
import { channelIdForThread, defaultChannelId, orderChannels } from '@/modules/simulator/thread/channels';
import { useChannels } from '@/hooks/use-channels';
import { useBusinessUnit } from '@/hooks/use-business-units';
import { useChat, useChatMessages, generateMessageId } from '@/hooks/use-chat';
import { useWebSocket } from '@/hooks/use-websocket';
import { useIntegrations } from '@/hooks/use-integrations';
import { currentUserAtom } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { simulatorRunUrl } from '@/constants/paths';
import { AlertTriangle, History, RotateCcw, Wifi, WifiOff } from 'lucide-react';
import type { MessageTypeValue, ChatMessage } from '@/types/websocket.types';

async function threadByConversationId(conversationId: number): Promise<LoadedThread | null> {
  // Not brand-scoped: the conversation list is account-wide, so the picker beside this
  // input is unscoped too and a scoped id lookup would be stricter than the list it
  // accompanies.
  const result = await messageService.getAllConversations(1, undefined, {
    conversationIdSearch: String(conversationId),
  });
  const conversation = result.conversations[0];
  if (!conversation) return null;
  const { messages } = await messageService.getConversationMessages(conversationId, 100);
  return threadFromConversation(conversation, messages.filter((m) => m.messageType !== 'activity' && m.content?.trim()));
}

async function threadByTicketId(externalTicketId: string): Promise<LoadedThread | null> {
  const thread = await ticketService.getThread(externalTicketId);
  return thread ? threadFromTicket(thread) : null;
}

/** The thread's own shape while channels load: empty state, Add Message, composer. */
const PlaygroundSkeleton = () => (
  <div className='flex h-full w-full mx-auto min-w-[300px] max-w-[760px] flex-col'>
    <div className='flex flex-1 flex-col gap-4 px-4 py-5'>
      <div className='flex flex-col items-center gap-3 pt-12'>
        <Skeleton className='h-12 w-12 rounded-full' />
        <Skeleton className='h-4 w-52' />
        <Skeleton className='h-3 w-72' />
      </div>
      <Skeleton className='mx-auto h-8 w-32 rounded-full' />
    </div>
    <div className='shrink-0 px-4 pt-2 pb-4'>
      <Skeleton className='h-13 w-full rounded-[20px]' />
    </div>
  </div>
);

export function SimulatorWorkbench({
  variant = 'playground',
}: {
  /** `playground` authors from scratch, `runner` replays a handed-over set, `standalone` shares neither. */
  variant?: 'playground' | 'runner' | 'standalone';
}) {
  const isRunner = variant === 'runner';
  // Past runs belong to the simulator page: reopening one from the workflow sheet would replace
  // the conversation the two share from inside another page.
  const browsesPastRuns = variant !== 'standalone';
  const currentUser = useAtomValue(currentUserAtom);
  const { data: channels, isLoading: channelsLoading } = useChannels();
  // The brand the run answers as: the global brand filter, not the channel picked, since
  // every brand owns its own channels. Null is "All", which runs account-level.
  const { buId } = useBusinessUnit();
  const { data: integrations } = useIntegrations();
  const orderedChannels = useMemo(() => orderChannels(channels ?? []), [channels]);
  const { messages } = useChatMessages();
  // The stored shape ({from}, number timestamp). The display `messages` above is a mapped
  // view ({role}, Date) without `from`, so anything fed back to setMessages comes from here.
  const { messages: rawMessages, addUserMessage, addAssistantMessage, addRunReply, updateMessage, removeMessage, setMessages } = useChat();
  const [selectedChannelId, setSelectedChannelId] = useAtom(channelIdAtom);
  const [conversationId, setConversationId] = useAtom(conversationIdAtom);
  const [customer, setCustomer] = useAtom(simulatorCustomerAtom);
  const [simProfile, setSimProfile] = useAtom(simulationProfileAtom);
  const [simulateTools, setSimulateTools] = useAtom(simulateToolsAtom);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadedThread, setLoadedThread] = useAtom(loadedThreadAtom);
  const [storedSession, setSession] = useAtom(simulatorSessionAtom);
  const session = isRunner ? storedSession : null;
  const activeCase = session && session.activeIndex !== null ? (session.cases[session.activeIndex] ?? null) : null;
  const clearActiveCase = useSetAtom(clearActiveCaseAtom);
  const endSimulatorSession = useSetAtom(endSimulatorSessionAtom);
  const setCaseOnScreen = useSetAtom(caseOnScreenAtom);
  const [reopenedLinks, setReopenedLinks] = useAtom(reopenedLinksAtom);
  const [described, setDescribed] = useAtom(describedScenarioAtom);
  const cancelDescribeFlow = useSetAtom(cancelDescribeFlowAtom);
  const settingUp = useAtomValue(describeFlowAtom).status !== 'idle';
  const setRunOnScreen = useSetAtom(runOnScreenAtom);
  const [pastRunsOpen, setPastRunsOpen] = useState(false);
  const reopenRun = useSetAtom(restoreRunAtom);
  const navigate = useNavigate();
  const setThinkingSteps = useSetAtom(thinkingStepsAtom);
  const setLastRunDetails = useSetAtom(lastRunDetailsAtom);

  const { sendMessage, setMessageHandler, setErrorHandler, isConnected, isWaiting, toolExecutionText, isToolExecuting, status,
    clearSegNodeStates, activeThinkingText, thinkingSteps, lastRunDetails, lastRunError, lastRunApproval } = useWebSocket();
  const resetSimulator = useSetAtom(resetSimulatorAtom);

  const [runReviews, setRunReviews] = useAtom(runReviewsAtom);
  const replySides = useAtomValue(replySideAtom);

  // The past ticket this run replays, from the loaded scenario, the sidebar or the reopened run.
  // Its thread is already fetched for the sidebar, so whether a human answered is a cache read.
  // Only `kind: 'ticket'`: an inbox conversation is keyed by conversation id, not an external ticket id.
  const reviewTicketId =
    activeCase?.ticketId ?? (loadedThread?.kind === 'ticket' ? loadedThread.id : null) ?? reopenedLinks?.ticketId ?? null;
  const scenarioKind = activeCase?.kind ?? reopenedLinks?.kind ?? null;
  // The type a described thread carries, written here or reopened with its run; a loaded case brings its own.
  const describedType = activeCase ? null : described.scenarioType;
  // Mocked holds from the moment the box opens: the describe flow only exists there.
  const toolModeLock = useToolModeLock(scenarioKind, Boolean(describedType) || settingUp);
  const { data: reviewThread } = useTicketThread(reviewTicketId);
  const hasAgentReply = reviewThread?.firstAgentReplyIndex !== null && reviewThread?.firstAgentReplyIndex !== undefined;
  const lastCustomerTurn = [...rawMessages].reverse().find((m) => m.from === 'user')?.content;

  // Once the judge has the agent's reply, the AI's bubble switches between the two in place.
  const replyView = useCallback(
    (replyRequestId: string) => {
      const review = runReviews[replyRequestId];
      if (review?.judge.status !== 'done') return undefined;
      const message = rawMessages.find((m) => m.requestId === replyRequestId);
      const side = replySides[replyRequestId] ?? 'ai';
      const { result, authored } = review.judge;
      return {
        ...comparedReplyView(side, {
          requestId: replyRequestId,
          aiReply: message?.content ?? '',
          agentReply: result.humanResponse,
          similarity: num(result.comparison['semanticSimilarity']),
          authored,
          highlights: missingPhrases(result.comparison),
          at: new Date(message?.timestamp ?? Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }),
        // Editing while the agent's words show would write them into this run's reply.
        editable: side === 'ai',
      };
    },
    [runReviews, replySides, rawMessages]
  );

  const renderReply = useCallback(
    (replyRequestId: string) => (
      <RunReview
        requestId={replyRequestId}
        aiReply={rawMessages.find((m) => m.requestId === replyRequestId)?.content ?? ''}
        ticketId={reviewTicketId}
        fabricated={scenarioKind === 'fabricated'}
        hasAgentReply={hasAgentReply}
        traceUrl={lastRunDetails?.requestId === replyRequestId ? lastRunDetails.traceUrl : undefined}
        customerQuestion={lastCustomerTurn}
      />
    ),
    [rawMessages, reviewTicketId, scenarioKind, hasAgentReply, lastRunDetails, lastCustomerTurn]
  );
  const setOpenSections = useSetAtom(openSectionsAtom);

  // Live state mirrored into refs: the socket handler correlates responses and the
  // role swap reads the thread, neither of which should re-subscribe on every
  // keystroke.
  const conversationIdRef = useRef(conversationId);
  const messagesRef = useRef(rawMessages);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);
  useEffect(() => {
    messagesRef.current = rawMessages;
  }, [rawMessages]);

  useEffect(() => {
    setMessageHandler((data) => {
      // A response for a conversation that is no longer on screen (the user
      // reset or loaded another case) must not append into this thread.
      if (data.conversation_id && String(data.conversation_id) !== conversationIdRef.current) return;
      if (data.success && data.response && data.response.length > 0) {
        addRunReply(data.response[0], data.rules_used, data.request_id);
      } else {
        const errorMessage = 'Sorry, I encountered an error processing your request.';
        addRunReply(errorMessage, undefined, undefined, true);
        toast.error('Failed to get response');
      }
    });

    setErrorHandler((data) => {
      toast.error(data.message || 'An error occurred');
      addRunReply(`Error: ${data.message || 'An unexpected error occurred'}`, undefined, undefined, true);
    });
  }, [setMessageHandler, setErrorHandler, addRunReply]);

  // Email leads the row and is what a fresh composer starts on; a loaded thread
  // overrides it with the channel that thread actually arrived on. The choice
  // outlives the workbench, so one this account does not have (left by an
  // earlier sign-in, or since deleted) falls back the same way.
  useEffect(() => {
    if (orderedChannels.length > 0 && !orderedChannels.some((c) => c.id === selectedChannelId)) {
      setSelectedChannelId(defaultChannelId(orderedChannels) ?? '');
    }
  }, [orderedChannels, selectedChannelId, setSelectedChannelId]);

  // Ask AI sends the thread exactly as it stands: turns are authored on the
  // thread itself, so there is never a pending one to append here.
  const publishThread = useCallback(() => {
    const accountId = currentUser?.accountId != null ? String(currentUser.accountId) : undefined;
    if (!accountId) {
      toast.error('Please log in to send messages');
      return;
    }
    if (!isConnected) {
      toast.error('Not connected to server');
      return;
    }
    const selectedChannel = channels?.find((c) => c.id === selectedChannelId);
    const shopifyIntegration = integrations?.find((i) => i.name.toLowerCase() === 'shopify' && i.isConnected);

    sendMessage({
      type: 'CRM_AI_ASSIST' as MessageTypeValue,
      payload: {
        messages: messages
          .filter((m) => !m.isError)
          .map((m) => ({
            role: m.role === 'user' ? ('user' as const) : ('bot' as const),
            content: [{ type: 'text' as const, text: m.content }],
          })),
        channel: selectedChannelId,
        // Validated against the account's own brands server-side before it is used.
        ...(buId && { bu_id: buId }),
        inboxId: selectedChannel?.inboxId,
        shopifyId: shopifyIntegration?.id ? Number(shopifyIntegration.id) : undefined,
        email: customer.email.trim() || undefined,
        name: customer.name.trim() || undefined,
        // Markers ride inside payload: the backend forwards payload verbatim for
        // non-TEST_RULE types (root attributes are dropped). Mocked sends the world;
        // Live data asks for live reads and simulated writes, and the backend adds the
        // loaded scenario's type for Brahma to bend the reads to.
        additionalAttributes: {
          source: 'playground_simulator',
          ...(simulateTools
            ? { simulate_tools: true, simulation_profile: buildSimulationProfile(simProfile) }
            : { ground_tools: true }),
        },
      },
      conversationId,
      accountId,
      channel: selectedChannelId,
      // Kept with the run by the backend so it can be reopened; not sent to Brahma.
      simulatorRun: {
        sopId: activeCase?.sopId ?? reopenedLinks?.sopId ?? null,
        scenarioId: activeCase?.id ?? reopenedLinks?.scenarioId ?? null,
        ticketId: reviewTicketId,
        kind: scenarioKind,
        simulationProfile: { ...simProfile },
        scenarioType: describedType,
      },
    });
  }, [selectedChannelId, channels, integrations, currentUser?.accountId, isConnected, sendMessage, conversationId, reopenedLinks, activeCase, reviewTicketId, scenarioKind, describedType, messages, customer, simProfile, simulateTools, buId]);

  // Loading a scenario case replaces the session: reset first so no stale card or
  // turn survives, then apply the case's thread and sidebar values.
  const handleLoadScenarioCase = useCallback(
    (scenarioCase: ScenarioCase) => {
      resetSimulator();
      // `<scenarioId>:<nonce>`: the scenario stays legible in traces while each
      // fresh load is its own conversation, so Brahma starts clean instead of
      // resuming the previous attempt's state. Asking again within this thread
      // keeps the id: that is the same conversation continuing.
      setConversationId(scenarioCase.id ? nextConversationId(scenarioCase.id) : crypto.randomUUID());
      setSimProfile({ ...EMPTY_SIMULATION_PROFILE, ...scenarioCase.simulationConfig });
      setMessages(
        scenarioCase.messages.map((m) => ({
          id: generateMessageId(),
          from: m.role === 'user' ? ('user' as const) : ('assistant' as const),
          content: m.text,
          timestamp: Date.now(),
          channelId: selectedChannelId,
        }))
      );
      setOpenSections([...filledSections(scenarioCase.simulationConfig), ...(scenarioCase.ticketId ? ['conversation'] : [])]);
    },
    [resetSimulator, setConversationId, setSimProfile, setMessages, setOpenSections, selectedChannelId]
  );

  // Putting a remembered case back: its own conversation id returns with it, so
  // a late response for it still correlates, and its run artefacts come back
  // rather than being regenerated.
  const restoreCaseRun = useCallback(
    (run: CaseRun, scenarioCase: ScenarioCase) => {
      setSimProfile({ ...EMPTY_SIMULATION_PROFILE, ...scenarioCase.simulationConfig });
      setOpenSections([...filledSections(scenarioCase.simulationConfig), ...(scenarioCase.ticketId ? ['conversation'] : [])]);
      setLoadedThread(null);
      setRunOnScreen(null);
      setConversationId(run.conversationId);
      setMessages(run.messages);
      setThinkingSteps(run.thinkingSteps);
      setLastRunDetails(run.runDetails);
      // Merged, not replaced: an investigation still running off screen keeps its entry.
      setRunReviews((prev) => ({ ...prev, ...run.reviews }));
    },
    [setSimProfile, setOpenSections, setLoadedThread, setRunOnScreen, setConversationId, setMessages, setThinkingSteps, setLastRunDetails, setRunReviews]
  );

  useCaseOnScreen({ session, isWaiting, load: handleLoadScenarioCase, restore: restoreCaseRun });

  const handleAddSimulatorMessage = useCallback(
    (role: SimulatorRole, text: string) => {
      if (role === 'user') {
        addUserMessage(text, selectedChannelId);
      } else {
        addAssistantMessage(text);
      }
    },
    [addUserMessage, addAssistantMessage, selectedChannelId]
  );

  // A thread with nothing behind it is described in words; a loaded ticket, scenario or run brings its own
  // world. The pill shows in either tool mode: opening the box holds the run on Mocked.
  const describable = !loadedThread && !activeCase && !reopenedLinks;

  const handleUpdateSimulatorMessage = useCallback(
    (id: string, text: string) => {
      updateMessage(id, { content: text });
    },
    [updateMessage]
  );

  // A turn authored on the wrong side is re-cast rather than retyped. The raw
  // messages carry `from`; the display view's `role` is derived from it.
  const handleSwapSimulatorMessageRole = useCallback(
    (id: string) => {
      const message = messagesRef.current.find((m) => m.id === id);
      if (message) updateMessage(id, { from: otherRole(message.from) });
    },
    [updateMessage]
  );

  const handleLoadThread = useCallback(
    (loaded: LoadedThread) => {
      const chatMessages: ChatMessage[] = loaded.messages.map((m) => ({
        id: generateMessageId(),
        from: m.role === 'user' ? 'user' : 'assistant',
        content: m.content,
        timestamp: new Date(m.createdAt).getTime() || Date.now(),
      }));
      setMessages(chatMessages);
      // A conversation rides on its real id (Brahma keys its state on it, no DB fetch);
      // a ticket with no conversation gets a fresh session id.
      setConversationId(loaded.conversationId != null ? String(loaded.conversationId) : crypto.randomUUID());
      setCustomer({ email: loaded.contact?.email ?? '', name: loaded.contact?.name ?? '' });
      // The thread already says which channel it came in on, so the composer follows it.
      // Anything unplaceable (a helpdesk ticket, or an inbox with no channel type) falls
      // back to the default rather than keeping what the previous thread left selected.
      setSelectedChannelId(channelIdForThread(orderedChannels, loaded.channel) ?? defaultChannelId(orderedChannels) ?? '');
      setLoadedThread(loaded);
      // What is on screen is no longer the loaded case, so its run must not be
      // remembered under that case's key.
      setCaseOnScreen(null);
      setReopenedLinks(null);
      // The thread is the ticket's now; a situation described before it is not.
      setDescribed(EMPTY_DESCRIBED_SCENARIO);
      cancelDescribeFlow();
      setRunOnScreen(null);
      clearSegNodeStates();
      setPickerOpen(false);
      toast.success(`Loaded ${loaded.kind} #${loaded.id}`);
    },
    [setMessages, setDescribed, cancelDescribeFlow, setConversationId, setCustomer, setSelectedChannelId, setLoadedThread, setCaseOnScreen, setReopenedLinks, setRunOnScreen, clearSegNodeStates, orderedChannels]
  );

  const handleClearMessages = useCallback(() => {
    resetSimulator();
    // A cleared composer is a fresh one, so the channel goes back to the default
    // rather than staying on whatever the thread just put away arrived on.
    setSelectedChannelId(defaultChannelId(orderedChannels) ?? '');
    // The runner keeps its set so the next case is one click away. `playground` has no
    // set of its own, so reset drops any left behind. `standalone` owns none and must not
    // reach one: it opens over other pages, where a set belongs to whoever loaded it.
    if (isRunner) clearActiveCase();
    else if (variant === 'playground') endSimulatorSession();
    setCaseOnScreen(null);
    toast.success('Simulator reset');
  }, [resetSimulator, setSelectedChannelId, clearActiveCase, endSimulatorSession, setCaseOnScreen, isRunner, variant, orderedChannels]);

  // A scenario page leaves its scenario for the run: the hand-off ends, so coming back to the
  // scenario's link loads it fresh, and the address moves to the run.
  const openPastRun = useCallback(
    (run: SimulatorRun) => {
      setPastRunsOpen(false);
      if (isRunner) endSimulatorSession();
      reopenRun(run);
      if (isRunner) navigate(simulatorRunUrl(run.requestId));
    },
    [isRunner, endSimulatorSession, reopenRun, navigate]
  );

  // A conversation id first, then the same id as a helpdesk ticket.
  const handleLoadTicketId = useCallback(
    async (rawId: string): Promise<boolean> => {
      const id = rawId.trim().replace(/^#/, '');
      if (!id) {
        toast.error('Enter a ticket id');
        return false;
      }
      try {
        // Only a numeric id can be a conversation id; other helpdesks issue opaque ones.
        const numeric = /^\d+$/.test(id) && Number.isSafeInteger(Number(id));
        const asConversation = numeric ? await threadByConversationId(Number(id)) : null;
        const loaded = asConversation ?? (await threadByTicketId(id));
        if (!loaded) {
          toast.error(`No conversation or ticket ${id} on this account`);
          return false;
        }
        if (!loaded.messages.length) {
          toast.error(`#${id} has no loadable messages`);
          return false;
        }
        resetSimulator();
        handleLoadThread(loaded);
        return true;
      } catch {
        toast.error('Failed to load the conversation');
        return false;
      }
    },
    [resetSimulator, handleLoadThread]
  );

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <div className='flex shrink-0 items-center justify-between border-b border-gray-500 px-4 py-3'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-1.5'>
            {isConnected ? (
              <>
                <Wifi className='h-4 w-4 text-green-500' />
                <span className='text-xs text-green-500'>Connected</span>
              </>
            ) : (
              <>
                <WifiOff className='h-4 w-4 text-muted-foreground' />
                <span className='text-xs text-muted-foreground'>{status}</span>
              </>
            )}
          </div>
          {!simulateTools && (
            <span className='flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700'>
              <AlertTriangle className='h-3 w-3' />
              Live data: real reads, simulated writes
            </span>
          )}
        </div>

        <div className='flex items-center gap-3'>
          {/* Shown in both modes: which scenario is loaded is a fact about the case,
              least worth hiding when reads are about to hit live systems. */}
          {describedType && <DescribedScenarioChip scenarioType={describedType} />}
          {session && (
            <ScenarioCaseLoader
              cases={session.cases}
              activeIndex={session.activeIndex}
              onSelect={(activeIndex) => {
                if (isWaiting) {
                  toast.error('A run is still in progress — wait for it to finish');
                  return;
                }
                setSession({ ...session, activeIndex });
              }}
            />
          )}

          {browsesPastRuns && (
            <Button variant='outline' size='sm' onClick={() => setPastRunsOpen(true)} className='gap-2'>
              <History className='h-4 w-4' />
              Past runs
            </Button>
          )}

          <Button
            variant='outline'
            size='sm'
            onClick={handleClearMessages}
            className='gap-2 text-destructive hover:text-destructive'
          >
            <RotateCcw className='h-4 w-4' />
            Reset
          </Button>
        </div>
      </div>

      <div className='flex min-h-0 flex-1 overflow-hidden'>
        <div className='flex min-w-0 flex-1 flex-col gap-4 overflow-hidden p-4'>
          {isToolExecuting && toolExecutionText && (
            <div className='flex items-center gap-2 px-4 py-2 bg-muted rounded-lg text-sm text-muted-foreground'>
              <div className='animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full' />
              <span>{toolExecutionText}</span>
            </div>
          )}
          <div className='flex min-h-0 flex-1 justify-center overflow-hidden'>
            {channelsLoading ? (
              <PlaygroundSkeleton />
            ) : (
              <Simulator
                messages={messages}
                isLoading={isWaiting}
                thinkingStep={activeThinkingText ?? undefined}
                thinkingSteps={thinkingSteps}
                approval={lastRunApproval}
                availableChannels={orderedChannels}
                selectedChannelId={selectedChannelId}
                onChannelChange={setSelectedChannelId}
                onAddMessage={handleAddSimulatorMessage}
                onUpdateMessage={handleUpdateSimulatorMessage}
                onDeleteMessage={removeMessage}
                onSwapMessageRole={handleSwapSimulatorMessageRole}
                onAskAi={publishThread}
                renderAfterReply={renderReply}
                replyView={replyView}
                emptyAction={describable ? <DescribeTrigger /> : undefined}
                emptyPanel={describable ? <DescribePanel /> : undefined}
                settingUp={settingUp}
              />
            )}
          </div>
        </div>
        <aside className='hidden lg:flex w-80 shrink-0 flex-col overflow-hidden'>
          <SimulatorSidebar
            details={lastRunDetails}
            loading={isWaiting}
            error={lastRunError}
            approval={lastRunApproval}
            customer={customer}
            onCustomerChange={setCustomer}
            profile={simProfile}
            onProfileChange={setSimProfile}
            simulateTools={simulateTools}
            onSimulateToolsChange={setSimulateTools}
            toolModeLock={toolModeLock}
            loadedThread={loadedThread}
            pastTicketId={activeCase?.ticketId ?? reopenedLinks?.ticketId ?? null}
            caseKey={conversationId}
            onOpenPicker={() => setPickerOpen(true)}
            onLoadTicketId={handleLoadTicketId}
          />
        </aside>
      </div>

      <ConversationPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onLoad={(conversation, messages) => handleLoadThread(threadFromConversation(conversation, messages))}
      />

      {browsesPastRuns && (
        <PastRunsSheet open={pastRunsOpen} onOpenChange={setPastRunsOpen} busy={isWaiting} onOpen={openPastRun} />
      )}
    </div>
  );
}
